import { useState } from 'react';
import { Link } from 'react-router';
import type { Coverage, CoverageItem } from '@ametica/shared';
import { useCoverage, useSiteCoverage } from '../../api/coverage';
import { Icon } from '../../components/Icon';
import { coverageSummary, type CoverageTone } from './coverage';

const TONE: Record<CoverageTone, string> = { ok: 'bg-ok', warn: 'bg-sev-medium', none: 'bg-ghost' };
const TYPE_LABEL: Record<CoverageItem['type'], string> = { plugin: 'plugin', theme: 'téma', core: 'core' };

/**
 * Panel pokrytia: čo matching NEVIE posúdiť. Bez `siteId` je globálny (prehľad),
 * so `siteId` per stránka (detail). Rozklik ukáže zoznam s názvom, slugom, verziou a stránkou.
 */
export function CoveragePanel({ siteId }: { siteId?: string }) {
  return siteId ? <SiteCoverage siteId={siteId} /> : <GlobalCoverage />;
}

function GlobalCoverage() {
  const q = useCoverage();
  return <Panel coverage={q.data} error={q.isError ? q.error.message : null} showSite />;
}

function SiteCoverage({ siteId }: { siteId: string }) {
  const q = useSiteCoverage(siteId);
  return <Panel coverage={q.data} error={q.isError ? q.error.message : null} showSite={false} />;
}

function Panel({ coverage, error, showSite }: { coverage: Coverage | undefined; error: string | null; showSite: boolean }) {
  const [open, setOpen] = useState(false);
  if (error) return <div className="card px-5 py-[18px] text-[12.5px] text-sev-critical">Pokrytie sa nepodarilo načítať: {error}</div>;
  if (!coverage) return <div className="card px-5 py-[18px]"><span className="card-t">Pokrytie katalógom</span></div>;
  const s = coverageSummary(coverage);
  const canOpen = coverage.items.length > 0;

  return (
    <div className="card">
      <div className="flex flex-col gap-3.5 px-5 py-[18px]">
        <div className="flex items-center justify-between gap-3">
          <span className="card-t">Pokrytie katalógom</span>
          <span className="mono text-[11px] text-faint">{coverage.covered} / {coverage.total} posúdených</span>
        </div>
        <button
          type="button"
          className={`flex items-start gap-2.5 rounded-[6px] py-1 text-left text-ink ${canOpen ? '' : 'cursor-default'}`}
          onClick={() => canOpen && setOpen((o) => !o)}
          aria-expanded={open}
          disabled={!canOpen}
        >
          <span className={`dotst mt-[6px] ${TONE[s.tone]}`} />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[12.5px]">{s.headline}</span>
            {s.unkeyable && <span className="text-[11.5px] text-sev-medium">{s.unkeyable}</span>}
            <span className="text-[11px] text-ghost">
              {s.tone === 'none' ? 'spusti sken stránky' : s.tone === 'ok' ? 'nula nálezov tu naozaj znamená nulu' : 'bez záznamu v katalógu ≠ bezpečný — najčastejšie iný slug než vo Wordfence'}
            </span>
          </span>
          {canOpen && <Icon name="chev" size={14} className={`ml-auto mt-1 shrink-0 text-ghost transition-transform ${open ? 'rotate-90' : ''}`} />}
        </button>
      </div>
      {open && (
        <div className="tblwrap border-t border-line">
          <table className="tbl">
            <thead>
              <tr><th>KOMPONENT</th><th>VERZIA</th>{showSite && <th>STRÁNKA</th>}<th>PREČO</th></tr>
            </thead>
            <tbody>
              {coverage.items.map((i) => (
                <tr key={`${i.siteId}:${i.type}:${i.slug}`}>
                  <td>
                    <div>{i.name || i.slug}{i.type !== 'plugin' && <span className="ml-2 text-[11px] text-faint">{TYPE_LABEL[i.type]}</span>}</div>
                    <div className="sub">{i.slug}</div>
                  </td>
                  <td className="mono w-[110px]">{i.version || <span className="text-faint">bez verzie</span>}</td>
                  {showSite && <td className="mono w-[160px] text-dim"><Link to={`/sites/${i.siteId}`}>{i.siteHost}</Link></td>}
                  <td className="w-[220px] text-[11.5px] text-faint">
                    {!i.inCatalog && <span className="pill bg-raised text-sev-medium">bez záznamu v katalógu</span>}
                    {!i.keyable && <span className={`pill bg-raised text-faint ${!i.inCatalog ? 'ml-1.5' : ''}`}>verzia sa nedá porovnať</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
